// src/components/ScopeOptions.tsx
// What a list is drawn from — everything, my own, a team, a project.
//
// A flat list of choices rather than a dropdown of its own: this renders as a
// section inside the page's settings menu, and a second popover opened from
// inside the first portals to the body, where the outer menu's dismissal reads
// a click in it as a click outside itself.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import type { ScopeContext, ScopeOption, ScopeEntity } from '@/features/scope/types';
import { SCOPE_OPTIONS, isScopeEqual } from '@/features/scope/logic';
import { useScope } from '@/components/ScopeProvider';

// ---------------------------------------------------------------------------
// Inline SVG icons (same pattern as TenantSwitcher)
// ---------------------------------------------------------------------------

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  globe: GlobeIcon,
  user: UserIcon,
  users: UsersIcon,
  team: UsersIcon,
  folder: FolderIcon,
  project: FolderIcon,
  link: LinkIcon,
  entity: LinkIcon,
};

function ScopeIcon({ icon, className }: { icon: string; className?: string }) {
  const Component = ICON_MAP[icon] ?? GlobeIcon;
  return <Component className={className} />;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ScopeOptionsProps {
  /** Additional teams to offer as a scope */
  teams?: ScopeEntity[];
  /** Additional projects to offer as a scope */
  projects?: ScopeEntity[];
}

/** The name of the current scope, for the row that leads to this list. */
export function useScopeLabel(): string {
  const { scope } = useScope();
  const { t } = useI18n();
  return scope.scopeEntity?.name ? scope.scopeEntity.name : t(scope.scopeLabel || 'scope.all');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ScopeOptions = observer(({ teams = [], projects = [] }: ScopeOptionsProps) => {
  const { scope, setScope } = useScope();
  const { t } = useI18n();

  // ---- Helpers ----

  const handleSelect = (next: ScopeContext) => {
    setScope(next);
  };

  const handleSelectOption = (option: ScopeOption) => {
    handleSelect({
      scopeType: option.type,
      scopeEntity: option.entity ?? null,
      scopeLabel: option.labelKey,
    });
  };

  const handleSelectEntity = (type: 'team' | 'project', entity: ScopeEntity) => {
    handleSelect({
      scopeType: type,
      scopeEntity: entity,
      scopeLabel: entity.name ? `scope.${type}Named` : `scope.${type}`,
    });
  };

  const isOptionActive = (option: ScopeOption): boolean => {
    return isScopeEqual(
      { scopeType: option.type, scopeEntity: option.entity ?? null, scopeLabel: option.labelKey },
      scope,
    );
  };

  const isEntityActive = (type: 'team' | 'project', entity: ScopeEntity): boolean => {
    return scope.scopeType === type && scope.scopeEntity?.id === entity.id;
  };

  // ---- Render ----

  return (
    <>
        {/* Static scope options */}
        <div>
          {SCOPE_OPTIONS.map((option) => (
            <button
              key={option.type}
              role="menuitem"
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors',
                isOptionActive(option)
                  ? 'bg-accent text-accent-foreground'
                  : 'text-foreground hover:bg-accent/50',
              )}
              onClick={() => handleSelectOption(option)}
            >
              <ScopeIcon icon={option.icon} className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{t(option.labelKey)}</span>
              {isOptionActive(option) && (
                <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
              )}
            </button>
          ))}
        </div>

        {/* Teams section */}
        {teams.length > 0 && (
          <div className="border-t border-border p-1">
            <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('scope.teams')}
            </p>
            {teams.map((team) => (
              <button
                key={team.id}
                role="menuitem"
                className={cn(
                  'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors',
                  isEntityActive('team', team)
                    ? 'bg-accent text-accent-foreground'
                    : 'text-foreground hover:bg-accent/50',
                )}
                onClick={() => handleSelectEntity('team', team)}
              >
                <UsersIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{team.name}</span>
                {isEntityActive('team', team) && (
                  <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Projects section */}
        {projects.length > 0 && (
          <div className="border-t border-border p-1">
            <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('scope.projects')}
            </p>
            {projects.map((project) => (
              <button
                key={project.id}
                role="menuitem"
                className={cn(
                  'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors',
                  isEntityActive('project', project)
                    ? 'bg-accent text-accent-foreground'
                    : 'text-foreground hover:bg-accent/50',
                )}
                onClick={() => handleSelectEntity('project', project)}
              >
                <FolderIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{project.name}</span>
                {isEntityActive('project', project) && (
                  <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                )}
              </button>
            ))}
          </div>
        )}
    </>
  );
});

ScopeOptions.displayName = 'ScopeOptions';
